import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import {
	Box,
	Chip,
	IconButton,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	type TableSortLabelOwnProps,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { type FC, type SyntheticEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChangeSourceBadge } from "../../components/UIToolkit/Badges/ChangeSourceBadge";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { SystemTextFieldProps } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { DateRangeSelector } from "../../components/UIToolkit/molecules/DateRangeSelector";
import type { Department } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { INK, NEUTRAL, STATUS, SURFACE } from "../../theme/tokens";
import type { Nurse } from "../../utils/models";
import { resolveFieldValue } from "./resolveFieldValue";
import { type EmployeeChangeLogField, type EmployeeChangelogEntry, useEmployeeChangelogQuery } from "./useEmployeeChangelogQuery";

type SortField = "changedAt" | "nurseName" | "eventType" | "changedBy" | "source";
type SortOrder = Exclude<TableSortLabelOwnProps["direction"], undefined>;

const FIELD_LABELS: Record<string, string> = {
	active: "Active status",
	email: "Email",
	phone_number: "Phone",
	role: "Role",
	job_ids: "Job",
	wage_type: "Wage type",
	wage: "Wage",
	hourly_wage: "Hourly wage",
	first_name: "First name",
	last_name: "Last name",
	employment_type: "Employment type",
	is_shift_manager: "Shift manager",
	training_status: "Training status",
	scheduled_deactivation_date: "Deactivation date",
	status_decision: "Status decision",
	department_ids: "Departments",
	shift_type: "Shift type",
	can_be_relief_charge: "Relief charge",
	gender: "Gender",
	date_of_birth: "Date of birth",
	marital_status: "Marital status",
	city: "City",
	street: "Street",
	zip_code: "Zip code",
	country: "Country",
	allowed_shifts: "Allowed shifts",
	prn_shifts_in_cycle: "PRN shifts/cycle",
	reliability_score: "Reliability score",
	agency_name: "Agency",
	union: "Union",
	no_weekly_preferences: "No weekly prefs",
	user_id: "User account",
	nursing_starting_date: "Nursing start date",
	is_on_schedule: "On schedule",
	extra_shifts: "Extra shifts",
};

/** Translated labels. New fields go here because the English map above is older than i18n. */
const TRANSLATED_FIELD_LABEL_KEYS: Record<string, string> = {
	silent_job_ids: "jobNotifications.changelog.field",
};

const isKnownField = (field: string): boolean => field in FIELD_LABELS || field in TRANSLATED_FIELD_LABEL_KEYS;

const fieldLabel = (field: string, t: TFunction): string => {
	const key = TRANSLATED_FIELD_LABEL_KEYS[field];
	if (key) return t(key);
	return FIELD_LABELS[field] ?? field.replace(/_/g, " ");
};

const INSERT_SUMMARY_FIELDS: EmployeeChangeLogField[] = ["first_name", "last_name", "role", "job_ids", "employment_type", "email", "phone_number", "active"];

const minWidth = { xs: 210, xxl: 300 } as const satisfies { xs: number | string; xxl: number | string };

const commonTextFieldProps: SystemTextFieldProps = {
	inputSx: { minWidth: minWidth },
	reserveHeightForHelperText: false,
};

const ExpandedDetail: FC<{ entry: EmployeeChangelogEntry; chosenDepartment: Department }> = ({ entry, chosenDepartment }) => {
	const { t } = useTranslation();
	const fields = (entry.changedFields ?? {}) as Record<EmployeeChangeLogField, unknown>;

	if (entry.eventType === "Insert") {
		const rows = INSERT_SUMMARY_FIELDS.filter((f) => fields[f] !== undefined && fields[f] !== null);
		return (
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, p: 2 }}>
				{rows.map((field) => (
					<Box key={field}>
						<Typography variant="caption" sx={{ color: NEUTRAL[500], display: "block" }}>
							{fieldLabel(field, t)}
						</Typography>
						<Typography variant="body2" sx={{ color: INK.SECONDARY, fontWeight: 500 }}>
							{resolveFieldValue(field, fields[field], chosenDepartment, t)}
						</Typography>
					</Box>
				))}
				{rows.length === 0 && (
					<Typography variant="body2" sx={{ color: NEUTRAL[500] }}>
						No profile details available.
					</Typography>
				)}
			</Box>
		);
	}

	const fieldKeys = Object.keys(fields).filter(isKnownField);
	if (fieldKeys.length === 0) return null;

	return (
		<Box sx={{ p: 2 }}>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "auto 1fr 1fr",
					gap: "2px 0",
					"& > *": { py: 0.75, px: 1.5 },
				}}
			>
				<Typography variant="caption" sx={{ fontWeight: 700, color: INK.SECONDARY, borderBottom: `1px solid ${NEUTRAL[200]}` }}>
					Field
				</Typography>
				<Typography variant="caption" sx={{ fontWeight: 700, color: STATUS.ERROR.dark, borderBottom: `1px solid ${NEUTRAL[200]}` }}>
					Before
				</Typography>
				<Typography variant="caption" sx={{ fontWeight: 700, color: STATUS.SUCCESS.main, borderBottom: `1px solid ${NEUTRAL[200]}` }}>
					After
				</Typography>

				{fieldKeys.map((field, i) => {
					const typedField = field as EmployeeChangeLogField;
					const change = fields[typedField] as { old?: unknown; new?: unknown } | undefined;
					const bg = i % 2 === 0 ? NEUTRAL[50] : SURFACE;
					const label = fieldLabel(field, t);
					return [
						<Typography key={`${field}-label`} variant="body2" sx={{ color: INK.SECONDARY, fontWeight: 600, bgcolor: bg }}>
							{label}
						</Typography>,
						<Typography key={`${field}-old`} variant="body2" sx={{ color: STATUS.ERROR.dark, bgcolor: bg, textDecoration: "line-through" }}>
							{resolveFieldValue(typedField, change?.old, chosenDepartment, t)}
						</Typography>,
						<Typography key={`${field}-new`} variant="body2" sx={{ color: STATUS.SUCCESS.main, bgcolor: bg }}>
							{resolveFieldValue(typedField, change?.new, chosenDepartment, t)}
						</Typography>,
					];
				})}
			</Box>
		</Box>
	);
};

const ChangedFieldsSummary: FC<{ entry: EmployeeChangelogEntry }> = ({ entry }) => {
	const { t } = useTranslation();
	if (entry.eventType === "Insert") {
		const fields = (entry.changedFields ?? {}) as Record<EmployeeChangeLogField, unknown>;
		const role = fields.role as string | undefined;
		const employmentType = fields.employment_type as string | undefined;
		const parts = [role, employmentType].filter(Boolean).join(" · ");
		return <Chip label={parts || "New employee"} size="small" sx={{ bgcolor: STATUS.SUCCESS.light, color: STATUS.SUCCESS.main, fontWeight: 600 }} />;
	}

	const diff = entry.changedFields;
	if (!diff || typeof diff !== "object") {
		return (
			<Typography variant="body2" sx={{ color: NEUTRAL[500], fontSize: "0.8rem" }}>
				Profile updated
			</Typography>
		);
	}

	const fieldKeys = Object.keys(diff).filter(isKnownField);
	if (fieldKeys.length === 0) {
		return (
			<Typography variant="body2" sx={{ color: NEUTRAL[500], fontSize: "0.8rem" }}>
				—
			</Typography>
		);
	}

	const summary = fieldKeys
		.slice(0, 3)
		.map((f) => fieldLabel(f, t))
		.join(", ");
	const overflow = fieldKeys.length > 3 ? ` +${fieldKeys.length - 3}` : "";

	return (
		<Typography variant="body2" sx={{ fontSize: "0.8rem", color: INK.SECONDARY }}>
			{summary}
			{overflow && (
				<Typography component="span" sx={{ color: NEUTRAL[500], fontSize: "0.75rem" }}>
					{overflow}
				</Typography>
			)}
		</Typography>
	);
};

const TableHeader: FC<{
	field: SortField;
	label: string;
	sortField: string;
	sortOrder: TableSortLabelOwnProps["direction"];
	handleSort: (_: SortField) => void;
}> = ({ field, label, sortField, sortOrder, handleSort }) => (
	<TableCell>
		<TableSortLabel
			active={sortField === field}
			direction={sortField === field ? sortOrder : "asc"}
			onClick={() => handleSort(field)}
			sx={{ "& .MuiTableSortLabel-icon": { color: `${INK.SECONDARY} !important` }, color: INK.SECONDARY, fontWeight: "bold" }}
		>
			{label}
		</TableSortLabel>
	</TableCell>
);

export const EmployeeFieldChangesView: FC = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { track } = useAnalytics();
	const navigate = useNavigate();

	const [start, setStart] = useState<DateTime>(DateTime.now().minus({ day: 30 }).startOf("day"));
	const [end, setEnd] = useState<DateTime>(DateTime.now().startOf("day"));
	const [importantOnly, setImportantOnly] = useState(false);
	// The picked option, label included: the rows' server names label it, so it needs no roster.
	const [nurseFilter, setNurseFilter] = useState<SystemAutoCompleteOption<Nurse["id"]> | null>(null);
	const [changedByFilter, setChangedByFilter] = useState<string>();
	const [sortField, setSortField] = useState<SortField>("changedAt");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
	const [expandedId, setExpandedId] = useState<string | null>(null);

	const { data: entries = [], isLoading } = useEmployeeChangelogQuery({
		start,
		end,
		nurseId: nurseFilter?.value,
		changedBy: changedByFilter,
	});

	const handleSort = (field: SortField) => {
		const newOrder = field === sortField && sortOrder === "asc" ? "desc" : "asc";
		setSortField(field);
		setSortOrder(newOrder);
		track("employee_changelog_sort_changed", { department_id: chosenDepartment?.department_id, sort_field: field, sort_order: newOrder });
	};

	const handleRowClick = (entry: EmployeeChangelogEntry) => {
		const next = expandedId === entry.id ? null : entry.id;
		setExpandedId(next);
		track("employee_changelog_row_clicked", {
			department_id: chosenDepartment?.department_id,
			nurse_id: entry.nurseId,
			event_type: entry.eventType,
			is_important: entry.isImportant,
			expanded: next !== null,
		});
	};

	const sorted = useMemo(() => {
		return entries.slice().sort((a, b) => {
			const dir = sortOrder === "asc" ? 1 : -1;
			if (sortField === "changedAt") {
				return DateTime.fromISO(a.changedAt).diff(DateTime.fromISO(b.changedAt), "minute").minutes * dir;
			}
			return ((a[sortField] ?? "") > (b[sortField] ?? "") ? 1 : -1) * dir;
		});
	}, [entries, sortField, sortOrder]);

	const nurseOptions = useMemo(
		() =>
			sorted
				.reduce<{ label: string; value: number }[]>((acc, entry) => {
					if (!acc.find((o) => o.value === entry.nurseId)) acc.push({ label: entry.nurseName, value: entry.nurseId });
					return acc;
				}, [])
				.sort((a, b) => a.label.localeCompare(b.label)),
		[sorted],
	);

	const changedByOptions = useMemo(
		() =>
			sorted
				.reduce<{ label: string; value: string }[]>((acc, entry) => {
					if (entry.changedBy && !acc.find((o) => o.value === entry.changedBy)) acc.push({ label: entry.changedBy, value: entry.changedBy });
					return acc;
				}, [])
				.sort((a, b) => a.label.localeCompare(b.label)),
		[sorted],
	);

	const formatTimestamp = (iso: string) => {
		const dt = DateTime.fromISO(iso);
		return (
			<Box component="span">
				<Typography component="span" sx={{ ml: 1 }}>
					{dt.toFormat("ccc, MMM d, yyyy")}
				</Typography>
				<Typography component="span" sx={{ opacity: 0.5, ml: 1 }}>
					{dt.toFormat("HH:mm:ss")}
				</Typography>
			</Box>
		);
	};

	const filtered = useMemo(() => {
		let result = importantOnly ? sorted.filter((e) => e.isImportant) : sorted;
		// Hide Update entries with no recognized changed fields
		result = result.filter((e) => {
			if (e.eventType === "Insert") return true;
			const fields = e.changedFields;
			if (!fields || typeof fields !== "object") return false;
			return Object.keys(fields).some(isKnownField);
		});
		return result;
	}, [sorted, importantOnly]);

	const COLSPAN = 9;

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2, minHeight: 0 }}>
			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: 2, backgroundColor: NEUTRAL[50], padding: "16px", borderRadius: "4px" }}>
				<Box sx={{ display: "flex", alignItems: "end", gap: 2, flexWrap: "wrap" }}>
					<DateRangeSelector
						label="Date range"
						startDate={start}
						endDate={end}
						onStartDateChange={(d) => {
							setStart(d);
							track("employee_changelog_date_filter_changed", { department_id: chosenDepartment?.department_id, filter_type: "from_date" });
						}}
						onEndDateChange={(d) => {
							setEnd(d);
							track("employee_changelog_date_filter_changed", { department_id: chosenDepartment?.department_id, filter_type: "to_date" });
						}}
						removeBackground
						containerSX={{ gap: 1 }}
					/>

					<Chip
						label="Important only"
						icon={<StarIcon sx={{ fontSize: "0.9rem !important" }} />}
						onClick={() => {
							const next = !importantOnly;
							setImportantOnly(next);
							track("employee_changelog_important_filter_toggled", { department_id: chosenDepartment?.department_id, important_only: next });
						}}
						variant={importantOnly ? "filled" : "outlined"}
						sx={(theme) => ({
							mb: 0.5,
							borderColor: theme.palette.primary.main,
							color: importantOnly ? SURFACE : theme.palette.primary.main,
							bgcolor: importantOnly ? theme.palette.primary.main : "transparent",
							"&:hover": { bgcolor: importantOnly ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.08) },
							fontWeight: 600,
						})}
					/>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(2,1fr)",
							gap: 1,
						}}
					>
						<SystemAutoComplete
							value={nurseFilter}
							onChange={(_: SyntheticEvent, value: SystemAutoCompleteOption<number> | null) => {
								setNurseFilter(value);
								track("employee_changelog_nurse_filter_changed", { department_id: chosenDepartment?.department_id, nurse_id: value?.value ?? "all" });
							}}
							options={nurseOptions}
							textFieldProps={{ ...commonTextFieldProps, label: "Employee" }}
						/>
						<SystemAutoComplete
							value={changedByFilter ? { label: changedByFilter, value: changedByFilter } : null}
							onChange={(_: SyntheticEvent, value: SystemAutoCompleteOption<string> | null) => {
								setChangedByFilter(value?.value);
								track("employee_changelog_changed_by_filter_changed", {
									department_id: chosenDepartment?.department_id,
									changed_by: value?.value ?? "all",
								});
							}}
							options={changedByOptions}
							textFieldProps={{ ...commonTextFieldProps, label: "Changed By" }}
						/>
					</Box>
				</Box>
			</Box>
			{/* Table */}
			<Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
				<TableContainer
					sx={{
						flex: 1,
						overflow: "auto",
						"&::-webkit-scrollbar": { width: "6px", height: "6px" },
						"&::-webkit-scrollbar-track": { background: NEUTRAL[100], borderRadius: "3px" },
						"&::-webkit-scrollbar-thumb": { background: NEUTRAL[500], borderRadius: "3px" },
						"&::-webkit-scrollbar-thumb:hover": { background: NEUTRAL[600] },
					}}
				>
					<Table
						size="small"
						stickyHeader
						sx={{
							tableLayout: "fixed",
							width: "100%",
							"& .MuiTableCell-root": { boxSizing: "border-box", padding: "10px", overflow: "hidden", textOverflow: "ellipsis", textAlign: "center" },
							"& thead th": {
								height: "36px",
								background: "var(--inhouse-primary-alpha-16)",
								borderBottom: `1px solid ${NEUTRAL[200]}`,
								color: INK.SECONDARY,
								fontSize: "0.875rem",
								fontWeight: "bold",
								padding: "13px 10px",
								textAlign: "center",
								position: "sticky",
								top: 0,
								zIndex: 1,
								"&:first-of-type": { width: "50px" },
								"&:nth-of-type(2)": { width: "40px" },
								"&:nth-of-type(3)": { width: "44px" },
							},
							"& tbody td": {
								height: "40px",
								background: SURFACE,
								borderBottom: `1px solid ${NEUTRAL[200]}`,
								color: INK.SECONDARY,
								fontSize: "0.875rem",
								textAlign: "center",
								py: 0.5,
							},
						}}
					>
						<TableHead>
							<TableRow>
								<TableCell>#</TableCell>
								<TableCell>
									<Tooltip title="Important change">
										<StarIcon sx={{ fontSize: "1rem", color: INK.SECONDARY }} />
									</Tooltip>
								</TableCell>
								{/* expand toggle */}
								<TableCell />
								<TableHeader field="changedAt" label="When" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableHeader field="nurseName" label="Employee" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableHeader field="eventType" label="Event" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableCell>Changed Fields</TableCell>
								<TableHeader field="changedBy" label="Changed By" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
								<TableHeader field="source" label="Source" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading
								? Array.from({ length: 10 }).map((_, i) => (
										<TableRow key={i.toString()}>
											{Array.from({ length: COLSPAN }).map((__, j) => (
												<TableCell key={j.toString()}>
													<Skeleton variant="text" width="75%" />
												</TableCell>
											))}
										</TableRow>
									))
								: filtered.slice(0, 500).map((entry, index) => {
										const isExpanded = expandedId === entry.id;
										const activeChange = entry.changedFields?.active as { old?: unknown; new?: unknown } | undefined;
										const wasDeactivated = activeChange && activeChange.new === false;
										const wasActivated = activeChange && activeChange.new === true;
										return [
											<TableRow
												key={entry.id}
												hover
												onClick={() => handleRowClick(entry)}
												sx={(theme) => ({
													cursor: "pointer",
													bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.08) : undefined,
													"& td:first-of-type": entry.isImportant ? { borderLeft: `3px solid ${theme.palette.primary.main}` } : {},
												})}
											>
												<TableCell>{index + 1}</TableCell>
												<TableCell>
													{entry.isImportant && (
														<Tooltip title="Important change">
															<StarIcon sx={(theme) => ({ fontSize: "1rem", color: theme.palette.primary.main })} />
														</Tooltip>
													)}
												</TableCell>
												<TableCell>
													<IconButton size="small" sx={{ p: 0.25 }}>
														{isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
													</IconButton>
												</TableCell>
												<TableCell>{formatTimestamp(entry.changedAt)}</TableCell>
												<TableCell>
													<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
														<Typography
															component="span"
															sx={(theme) => ({
																fontWeight: entry.eventType === "Insert" ? 600 : 400,
																color: INK.SECONDARY,
																cursor: "pointer",
																"&:hover": { textDecoration: "underline", color: theme.palette.primary.main },
															})}
															onClick={(e) => {
																e.stopPropagation();
																navigate(`/${chosenDepartment.department_id}/nurse/${entry.nurseId}`);
																track("employee_changelog_nurse_name_clicked", {
																	department_id: chosenDepartment?.department_id,
																	nurse_id: entry.nurseId,
																});
															}}
														>
															{entry.nurseName}
														</Typography>
														{wasDeactivated && (
															<Chip
																label="Inactive"
																size="small"
																sx={{ bgcolor: STATUS.ERROR.light, color: STATUS.ERROR.dark, fontWeight: 600, fontSize: "0.7rem", height: 20 }}
															/>
														)}
														{wasActivated && (
															<Chip
																label="Activated"
																size="small"
																sx={{ bgcolor: STATUS.SUCCESS.light, color: STATUS.SUCCESS.main, fontWeight: 600, fontSize: "0.7rem", height: 20 }}
															/>
														)}
													</Box>
												</TableCell>
												<TableCell>
													<Chip
														label={entry.eventType === "Insert" ? "Created" : "Updated"}
														size="small"
														sx={{
															bgcolor: entry.eventType === "Insert" ? STATUS.SUCCESS.light : STATUS.INFO.light,
															color: entry.eventType === "Insert" ? STATUS.SUCCESS.main : STATUS.INFO.dark,
															fontWeight: 600,
														}}
													/>
												</TableCell>
												<TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
													<ChangedFieldsSummary entry={entry} />
												</TableCell>
												<TableCell>{entry.changedBy ?? "—"}</TableCell>
												<TableCell>
													<ChangeSourceBadge source={entry.source} />
												</TableCell>
											</TableRow>,
											isExpanded && (
												<TableRow key={`${entry.id}-detail`} sx={(theme) => ({ bgcolor: alpha(theme.palette.primary.main, 0.04) })}>
													<TableCell colSpan={COLSPAN} sx={{ p: 0 }}>
														<ExpandedDetail entry={entry} chosenDepartment={chosenDepartment} />
													</TableCell>
												</TableRow>
											),
										];
									})}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Box>
	);
};
